'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Cart } from '@/lib/shopify/types';
import {
  addToCartClient,
  createCartClient,
  getCartClient,
  removeFromCartClient,
  updateCartClient,
} from '@/lib/shopify/client';

interface CartLine {
  variantId: string;
  quantity: number;
  attributes?: { key: string; value: string }[];
}

interface CartContextType {
  cart: Cart | null;
  isLoading: boolean;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (variantId: string, quantity?: number, attributes?: { key: string; value: string }[]) => Promise<void>;
  addItems: (lines: CartLine[]) => Promise<void>;
  updateItem: (lineId: string, quantity: number) => Promise<void>;
  removeItem: (lineId: string) => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_ID_KEY = 'shopify_cart_id';

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCartState] = useState<Cart | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  const setCart = useCallback((next: Cart | null) => {
    setCartState(next);
    if (typeof window !== 'undefined') {
      if (next?.id) {
        localStorage.setItem(CART_ID_KEY, next.id);
      } else {
        localStorage.removeItem(CART_ID_KEY);
      }
    }
  }, []);

  useEffect(() => {
    const initializeCart = async () => {
      const cartId = localStorage.getItem(CART_ID_KEY);

      if (cartId) {
        try {
          const response = await fetch(`/api/cart?cartId=${cartId}`);
          const data = await response.json();

          if (data.cart) {
            setCart(data.cart);
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error fetching cart:', error);
        }
      }

      try {
        const response = await fetch('/api/cart', { method: 'POST' });
        const data = await response.json();

        if (data.cart) {
          setCart(data.cart);
        }
      } catch (error) {
        console.error('Error creating cart:', error);
      }

      setIsLoading(false);
    };

    initializeCart();
  }, [setCart]);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const addItem = useCallback(
    async (variantId: string, quantity: number = 1, attributes?: { key: string; value: string }[]) => {
      if (!cart) return;

      setIsLoading(true);

      try {
        const response = await fetch('/api/cart/add', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cartId: cart.id,
            variantId,
            quantity,
            attributes,
          }),
        });

        const data = await response.json();

        if (data.cart) {
          setCart(data.cart);
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
      }

      setIsLoading(false);
    },
    [cart, setCart]
  );

  const addItems = useCallback(
    async (lines: CartLine[]) => {
      if (!cart || lines.length === 0) return;

      setIsLoading(true);

      try {
        const response = await fetch('/api/cart/add', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cartId: cart.id,
            lines,
          }),
        });

        const data = await response.json();

        if (data.cart) {
          setCart(data.cart);
        }
      } catch (error) {
        console.error('Error adding items to cart:', error);
      }

      setIsLoading(false);
    },
    [cart, setCart]
  );

  const updateItem = useCallback(
    async (lineId: string, quantity: number) => {
      if (!cart) return;

      setIsLoading(true);

      try {
        const response = await fetch('/api/cart/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cartId: cart.id,
            lineId,
            quantity,
          }),
        });

        const data = await response.json();

        if (data.cart) {
          setCart(data.cart);
        }
      } catch (error) {
        console.error('Error updating cart:', error);
      }

      setIsLoading(false);
    },
    [cart, setCart]
  );

  const removeItem = useCallback(
    async (lineId: string) => {
      if (!cart) return;

      setIsLoading(true);

      try {
        const response = await fetch('/api/cart/remove', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cartId: cart.id,
            lineId,
          }),
        });

        const data = await response.json();

        if (data.cart) {
          setCart(data.cart);
        }
      } catch (error) {
        console.error('Error removing from cart:', error);
      }

      setIsLoading(false);
    },
    [cart, setCart]
  );

  return (
    <CartContext.Provider
      value={{
        cart,
        isLoading,
        isOpen,
        openCart,
        closeCart,
        addItem,
        addItems,
        updateItem,
        removeItem,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);

  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }

  return context;
}
